import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  IconButton,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Link,
  Switch,
  FormControlLabel,
} from "@mui/material";
import { Save, ArrowBack, DeleteOutline } from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import {
  createServiceLocation,
  updateServiceLocation,
  getServiceLocationById,
} from "../../services/Api";
import {
  GoogleMap,
  useJsApiLoader,
  DrawingManager,
  Polygon,
  Autocomplete,
} from "@react-google-maps/api";

const libraries = ["drawing", "places"];

const mapContainerStyle = {
  width: "100%",
  height: "500px",
  borderRadius: "8px",
};

const defaultCenter = {
  lat: 12.9716,
  lng: 77.5946,
};

const CreateAndModifyServiceLocations = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEdit);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    polygon: [],
    isActive: true,
  });
  const [googleAuthError, setGoogleAuthError] = useState(null);
  const [isDrawingMode, setIsDrawingMode] = useState(false);

  const polygonRef = useRef(null);
  const mapRef = useRef(null);
  const autocompleteRef = useRef(null);
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY?.trim();
  const isApiKeyMissing = !apiKey;

  const { isLoaded, loadError } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: apiKey || "",
    libraries: libraries,
    version: "3.64",
  });

  const showMap = isLoaded && !isApiKeyMissing && !googleAuthError;

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.gm_authFailure = () => {
        const message = `[Google Maps] Authorization Failed! Check Google Cloud Console:
1. Go to console.cloud.google.com → Select your project
2. APIs & Services → Credentials → Find your API key "${apiKey?.substring(0, 10)}..."
3. Verify: 
   - Maps JavaScript API is ENABLED (APIs & Services → Library)
   - Billing is ACTIVE and not past due
   - No HTTP referrer restrictions blocking localhost or your domain
4. Try regenerating the key if issues persist`;
        console.error(message);
        setGoogleAuthError(
          "Google Maps API authorization failed. Check browser console for detailed steps.",
        );
      };
    }
    return () => {
      if (typeof window !== "undefined" && window.gm_authFailure) {
        delete window.gm_authFailure;
      }
    };
  }, [apiKey]);

  useEffect(() => {
    const loadInitialData = async () => {
      if (isEdit) {
        try {
          const locRes = await getServiceLocationById(id);
          if (locRes.success || locRes.data || locRes.name) {
            const data = locRes.data || locRes;
            setFormData({
              name: data.name || "",
              polygon: Array.isArray(data.polygon) ? data.polygon : [],
              isActive: data.isActive !== undefined ? data.isActive : true,
            });
          }
        } catch (err) {
          console.error(err);
          setError(err.response?.data?.message || err.message || "Failed to load data");
        } finally {
          setFetching(false);
        }
      }
    };
    loadInitialData();
  }, [id, isEdit]);

  const onPolygonComplete = useCallback((polygon) => {
    const path = polygon.getPath();
    const newPoly = [];
    for (let i = 0; i < path.getLength(); i++) {
      newPoly.push({
        lat: path.getAt(i).lat(),
        lng: path.getAt(i).lng(),
      });
    }
    setFormData((prev) => ({ ...prev, polygon: newPoly }));
    // Prevent drawing multiple polygons natively
    polygon.setMap(null);
    setIsDrawingMode(false);
  }, []);

  const onEdit = useCallback(() => {
    if (polygonRef.current) {
      const nextPath = polygonRef.current
        .getPath()
        .getArray()
        .map((latLng) => ({ lat: latLng.lat(), lng: latLng.lng() }));
      setFormData((prev) => ({ ...prev, polygon: nextPath }));
    }
  }, [polygonRef]);

  // const onPolygonLoad = useCallback((polygon) => {
  //   polygonRef.current = polygon;
  // }, []);


  const onPolygonLoad = useCallback((polygon) => {
    polygonRef.current = polygon;

    const path = polygon.getPath();

    path.addListener("set_at", () => {
      const points = path.getArray().map((p) => ({
        lat: p.lat(),
        lng: p.lng(),
      }));

      setFormData((prev) => ({
        ...prev,
        polygon: points,
      }));
    });

    path.addListener("insert_at", () => {
      const points = path.getArray().map((p) => ({
        lat: p.lat(),
        lng: p.lng(),
      }));

      setFormData((prev) => ({
        ...prev,
        polygon: points,
      }));
    });

    path.addListener("remove_at", () => {
      const points = path.getArray().map((p) => ({
        lat: p.lat(),
        lng: p.lng(),
      }));

      setFormData((prev) => ({
        ...prev,
        polygon: points,
      }));
    });
  }, []);

  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
  }, []);

  const onPlaceChanged = useCallback(() => {
    if (autocompleteRef.current && mapRef.current) {
      const place = autocompleteRef.current.getPlace();
      if (place && place.geometry) {
        mapRef.current.panTo({
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng(),
        });
        mapRef.current.setZoom(13);
      }
    }
  }, []);

  const handleClearPolygon = () => {
    setFormData((prev) => ({ ...prev, polygon: [] }));
    setIsDrawingMode(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.polygon.length < 3) {
      setError("Please draw a valid polygon on the map (at least 3 points).");
      return;
    }

    console.log(
      "Final Polygon Before Save:",
      formData.polygon.length,
      formData.polygon
    );

    setLoading(true);
    setError(null);
    setSuccess(false);

    const payload = {
      name: formData.name,
      polygon: formData.polygon,
      isActive: formData.isActive,
    };

    try {
      let response;
      if (isEdit) {
        response = await updateServiceLocation(id, payload);
      } else {
        response = await createServiceLocation(payload);
      }

      if (
        response.success ||
        response.name ||
        response.id ||
        response._id ||
        response.isActive !== undefined
      ) {
        setSuccess(true);
        setTimeout(() => navigate("/service-location"), 1500);
      } else {
        setError(response.message || "Failed to save service location");
      }
    } catch (err) {
      setError(err.response?.data?.message || "An error occurred while saving");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
          <Link
            underline="hover"
            color="inherit"
            href="#"
            onClick={(e) => {
              e.preventDefault();
              navigate("/service-location");
            }}
          >
            Service Locations
          </Link>
          <Typography color="text.primary">
            {isEdit ? "Edit" : "Create"} Location
          </Typography>
        </Breadcrumbs>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <IconButton
            onClick={() => navigate("/service-location")}
            sx={{ border: "1px solid", borderColor: "text.border" }}
          >
            <ArrowBack />
          </IconButton>
          <Typography variant="h4" sx={{ fontWeight: 300 }}>
            {isEdit ? "Edit Service Location" : "Create Service Location"}
          </Typography>
        </Box>
      </Box>

      <Paper
        elevation={0}
        sx={{
          p: 4,
          border: "1px solid",
          borderColor: "text.border",
          borderRadius: 2,
        }}
      >
        <form onSubmit={handleSubmit}>
          <Grid container spacing={4}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                Location Name
              </Typography>
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                placeholder="Enter service location name"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                required
              />
            </Grid>

            <Grid
              size={{ xs: 12, md: 6 }}
              sx={{
                display: "flex",
                alignItems: "flex-end",
                justifyContent: "flex-end",
                gap: 2,
              }}
            >
              <Box
                sx={{
                  mr: "auto",
                  display: "flex",
                  alignItems: "flex-end",
                  pb: 1,
                }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isActive}
                      onChange={(e) =>
                        setFormData({ ...formData, isActive: e.target.checked })
                      }
                      color="primary"
                    />
                  }
                  label={formData.isActive ? "Active" : "Inactive"}
                />
              </Box>
              {formData.polygon.length > 0 ? (
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteOutline />}
                  onClick={handleClearPolygon}
                >
                  Clear Polygon
                </Button>
              ) : (
                <Button
                  variant={isDrawingMode ? "contained" : "outlined"}
                  color="primary"
                  onClick={() => setIsDrawingMode(!isDrawingMode)}
                >
                  {isDrawingMode ? "Cancel Drawing" : "Draw Polygon"}
                </Button>
              )}
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography
                variant="subtitle2"
                sx={{ mb: 1, mt: 2, fontWeight: 700 }}
              >
                Geo Fencing Area
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mb: 2 }}
              >
                {formData.polygon.length === 0
                  ? "Draw a polygon on the map by clicking anchors. Connect back to the first anchor to finish."
                  : "Drag the polygon anchors to edit the area. Click 'Clear Polygon' to redraw."}
              </Typography>
              {(isApiKeyMissing || loadError || googleAuthError) && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {isApiKeyMissing
                    ? "Google Maps API key is missing. Add REACT_APP_GOOGLE_MAPS_API_KEY to .env and restart the app."
                    : googleAuthError
                      ? googleAuthError
                      : loadError
                        ? `Maps load error: ${loadError?.message || "Unknown error"}`
                        : "Google Maps failed to load."}
                  {googleAuthError && (
                    <div style={{ marginTop: 8, fontSize: "0.85em" }}>
                      📍 Check Browser Console for detailed troubleshooting
                      steps.
                    </div>
                  )}
                </Alert>
              )}
              <Box
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  overflow: "hidden",
                }}
              >
                {showMap ? (
                  <Box sx={{ position: "relative" }}>
                    <Box
                      sx={{
                        position: "absolute",
                        top: 10,
                        left: "50%",
                        transform: "translateX(-50%)",
                        zIndex: 1,
                        width: "400px",
                        maxWidth: "90%",
                        boxShadow: 3,
                        borderRadius: "4px",
                      }}
                    >
                      <Autocomplete
                        onLoad={(ac) => {
                          autocompleteRef.current = ac;
                        }}
                        onPlaceChanged={onPlaceChanged}
                      >
                        <TextField
                          fullWidth
                          placeholder="Search for a city or area..."
                          variant="outlined"
                          size="small"
                          sx={{
                            backgroundColor: "white",
                            borderRadius: 1,
                            "& fieldset": { border: "none" },
                          }}
                        />
                      </Autocomplete>
                    </Box>
                    <GoogleMap
                      mapContainerStyle={mapContainerStyle}
                      center={
                        formData.polygon.length > 0
                          ? formData.polygon[0]
                          : defaultCenter
                      }
                      zoom={11}
                      onLoad={onMapLoad}
                    >
                      <DrawingManager
                        onPolygonComplete={onPolygonComplete}
                        options={{
                          drawingControl: false,
                          polygonOptions: {
                            fillColor: "#FF5722",
                            fillOpacity: 0.3,
                            strokeWeight: 2,
                            strokeColor: "#FF5722",
                            clickable: false,
                            editable: true,
                            zIndex: 1,
                          },
                          drawingMode: isDrawingMode ? "polygon" : null,
                        }}
                      />
                      {formData.polygon.length > 0 && (
                        <Polygon
                          path={formData.polygon}
                          editable={true}
                          onLoad={onPolygonLoad}
                          onMouseUp={onEdit}
                          onDragEnd={onEdit}
                          options={{
                            fillColor: "#FF5722",
                            fillOpacity: 0.3,
                            strokeColor: "#FF5722",
                            strokeOpacity: 0.8,
                            strokeWeight: 2,
                          }}
                        />
                      )}
                    </GoogleMap>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      height: 500,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: "rgba(0,0,0,0.02)",
                    }}
                  >
                    {loadError || isApiKeyMissing || googleAuthError ? (
                      <Typography color="text.secondary">
                        Unable to load Google Maps. See the error above.
                      </Typography>
                    ) : (
                      <CircularProgress />
                    )}
                  </Box>
                )}
              </Box>

              {/* Coordinates Output Box */}
              {formData.polygon.length > 0 && (
                <Box
                  sx={{
                    mt: 2,
                    p: 2,
                    backgroundColor: "rgba(0,0,0,0.02)",
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 600, mb: 1 }}
                  >
                    Selected Coordinates:
                  </Typography>
                  <Grid container spacing={1}>
                    {formData.polygon.map((point, idx) => (
                      <Grid size={{ xs: 12, sm: 6, md: 4 }} key={idx}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: "monospace",
                            p: 1,
                            backgroundColor: "white",
                            borderRadius: 1,
                            border: "1px solid",
                            borderColor: "divider",
                          }}
                        >
                          <strong>Pt {idx + 1}:</strong> {point.lat?.toFixed(5)}
                          , {point.lng?.toFixed(5)}
                        </Typography>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}
            </Grid>

            <Grid size={{ xs: 12 }}>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}
              {success && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  Service location saved successfully!
                </Alert>
              )}

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 2,
                  mt: 4,
                }}
              >
                <Button
                  variant="outlined"
                  onClick={() => navigate("/service-location")}
                  sx={{ px: 4 }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading || formData.polygon.length === 0}
                  startIcon={
                    loading ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <Save />
                    )
                  }
                  sx={{ px: 4, backgroundColor: "primary.main" }}
                >
                  {isEdit ? "Update Location" : "Save Location"}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Box>
  );
};

export default CreateAndModifyServiceLocations;
